import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { FileText } from "lucide-react";

import { UICallout, UIDocumentRow } from "../../../ui";
import PaymentNotice from "../PaymentNotice";
import { formatDate } from "../../../../utils/Helpers";
import { mapPaymentRequest } from "../../../PublishingRequests/publishingRequestsUtils";
import {
  fillTemplate,
  formatAmount,
} from "../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import { EVENT_PAYMENT_STATES, PAYMENT_REQUEST_STATUSES } from "./paymentStateUtils";

import "./_EventPaymentState.scss";

export const formatPaymentDay = (value, lang) =>
  value ? formatDate(value, lang === "ar" ? "DD-MM-YYYY" : "MMM D, YYYY") : "";

/** Label / value pairs under the notice (amount submitted). */
const Details = ({ rows }) => (
  <dl className="event-payment-state__details">
    {rows.map(({ label, value }) => (
      <div key={label} className="event-detail-field">
        <dt className="event-detail-label">{label}</dt>
        <dd className="event-detail-value">{value || "—"}</dd>
      </div>
    ))}
  </dl>
);

Details.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ).isRequired,
};

/**
 * Payment state inside the Basic Information card (PROLINE-3923): Draft (with the reason of a
 * rejected request) or Pending (with the amount and evidence sent). Once approved, the paid
 * amount and date are plain fields of the card instead.
 */
const EventPaymentState = ({
  t,
  lang,
  state,
  submittedAt,
  request,
  currency,
  downloadLabel,
  onSubmitNewEvidence,
}) => {
  const amount = request
    ? `${formatAmount(Number(request.total_amount ?? 0))} ${currency}`
    : "";

  const copy = t[state];
  const isPending = state === EVENT_PAYMENT_STATES.pending;
  const line = isPending
    ? copy.line.replace("{date}", formatPaymentDay(submittedAt, lang))
    : copy.line;
  const rejected =
    state === EVENT_PAYMENT_STATES.draft &&
    request?.status === PAYMENT_REQUEST_STATUSES.rejected
      ? request
      : null;
  const evidence = isPending && request ? mapPaymentRequest(request).payment_proof : null;

  // Rejected: the payment was sent, so the draft copy ("payment not yet submitted") doesn't
  // apply. Same notice as a rejected crew size upgrade (Crew Size tab).
  if (rejected) {
    return (
      <div className="event-payment-state">
        <PaymentNotice
          variant="error"
          className="event-payment-state__rejected"
          title={t.rejected.title}
          description={
            <>
              {fillTemplate(t.rejected.message, {
                // Figma: 22-03-2026 in both languages, like the upgrade notice.
                date: rejected.reviewed_at
                  ? formatDate(rejected.reviewed_at, "DD-MM-YYYY")
                  : "",
                reason: rejected.rejection_reason ?? "",
              })}
              {/* A link right after the message, back to the payment step (same amount due). */}
              {onSubmitNewEvidence && (
                <>
                  {" "}
                  {/* A plain button: a UIButton's fixed height would make the line taller. */}
                  <button
                    type="button"
                    className="event-payment-state__link"
                    onClick={onSubmitNewEvidence}
                  >
                    {t.rejected.action}
                  </button>
                </>
              )}
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="event-payment-state">
      <Typography component="p" className="text-body-sm event-payment-state__line">
        {line}
      </Typography>
      <UICallout
        variant="warning"
        hasBorder={false}
        className="event-payment-state__notice"
        icon={<WarningAmberRoundedIcon fontSize="small" />}
        title={copy.title}
        description={
          <ul className="event-payment-state__points">
            {copy.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        }
      />
      {isPending && request && (
        <>
          <Details rows={[{ label: copy.amountSubmitted, value: amount }]} />
          {evidence && (
            <UIDocumentRow
              title={evidence.name}
              subtitle={copy.transferEvidence}
              href={evidence.url}
              downloadLabel={downloadLabel}
              icon={<FileText strokeWidth={1.75} />}
            />
          )}
        </>
      )}
    </div>
  );
};

EventPaymentState.propTypes = {
  /** `events.viewEvents.paymentState` copy. */
  t: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  state: PropTypes.oneOf([EVENT_PAYMENT_STATES.draft, EVENT_PAYMENT_STATES.pending])
    .isRequired,
  submittedAt: PropTypes.string,
  /** The event's latest publishing request (`last_publish_payment_request`). */
  request: PropTypes.shape({
    status: PropTypes.string,
    total_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    payment_proof_url: PropTypes.string,
    rejection_reason: PropTypes.string,
    reviewed_at: PropTypes.string,
  }),
  currency: PropTypes.string,
  downloadLabel: PropTypes.string,
  /** Opens the payment step again after a rejection; omitted without edit permission. */
  onSubmitNewEvidence: PropTypes.func,
};

EventPaymentState.defaultProps = {
  submittedAt: undefined,
  request: null,
  currency: "",
  downloadLabel: "",
  onSubmitNewEvidence: undefined,
};

export default EventPaymentState;
